import React, { useRef, useState } from 'react';
import Modal from '$/components/base/Modal';
import IconFont from '$/components/IconFont';
import NextImage from '$/components/NextImage';
import VideoCom from '$/components/VideoCom';
import { useLanguageStore } from '$/store/languageStore';
import { resolveAssetFileExtension } from '$/utils/assetLibraryCardLayout';
import { getVideoCoverUrl } from '$/utils/upload';
import type { BatchImageItem } from '@/store/useAIWatermarkStore';
import { replaceTemplateParams } from '@/utils';
import styles from './index.module.scss';

export type CardStatus =
  | 'normal'
  | 'processing'
  | 'success'
  | 'failed'
  | 'unrecognized'
  | 'downloadable';

export interface BatchProcessCardProps {
  item: BatchImageItem;
  selectable?: boolean;
  onToggleSelect?: () => void;
  onPreviewClick?: () => void;
  onDelete?: () => void;
  /** 结果图加载完成时调用，用于关闭 resultImageLoading 遮罩 */
  onResultImageLoad?: () => void;
  /** 抠图批量：卡片底色（透明 PNG 叠在纯色/棋盘格上） */
  previewBackground?: string;
  /** 抠图结果建议使用 contain，避免裁切透明图主体 */
  imageObjectFit?: 'cover' | 'contain';
}

interface VideoControls {
  play: () => void;
  pause: () => void;
  seek: (t: number) => void;
}

export default function BatchProcessCard({
  item,
  selectable = false,
  onToggleSelect,
  onPreviewClick,
  onDelete,
  onResultImageLoad,
  previewBackground,
  imageObjectFit = 'cover',
}: BatchProcessCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoControlRef = useRef<VideoControls | null>(null);
  const videoHostRef = useRef<HTMLDivElement | null>(null);
  const {
    url,
    type = 'image',
    coverUrl,
    status,
    errorText: rawErrorText,
    selected = false,
    resultUrl,
    resultImageLoading = false,
  } = item;
  const displayUrl = resultUrl || url;
  const isVideo = type === 'video';
  const posterSrc = isVideo ? (coverUrl || getVideoCoverUrl(displayUrl)) : displayUrl;
  // 展示文件类型后缀角标（如 JPG / MP4），优先从 URL 解析，失败时按素材类型兜底
  const suffixLabel = resolveAssetFileExtension(displayUrl, isVideo ? 'video' : 'image')
    .trim()
    .toUpperCase();

  const showLoadingOverlay = status === 'processing' || (status === 'success' && resultUrl && resultImageLoading);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const errorText = rawErrorText || replaceTemplateParams(
    languageStore.smartcatTranslationText((v) => v.not_recognized),
    { area: 'XX' },
  );

  const closeModal = () => setIsModalOpen(false);

  const handleCardClick = () => {
    if (selectable && onToggleSelect) {
      onToggleSelect();
    } else if (!selectable && onPreviewClick) {
      onPreviewClick();
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(true);
  };

  // 与 MasonryAssetCover 的视频悬停行为一致：进入播放、离开暂停并回到第 0 帧，同步恢复 poster 避免 Safari 黑帧
  const handleVideoMouseEnter = () => {
    const c = videoControlRef.current;
    if (!c) return;
    const poster = videoHostRef.current?.querySelector<HTMLElement>('.xgplayer-poster');
    if (poster) poster.style.opacity = '0';
    c.seek(0);
    c.play();
  };

  const handleVideoMouseLeave = () => {
    const c = videoControlRef.current;
    if (c) {
      c.pause();
      c.seek(0);
    }
    const poster = videoHostRef.current?.querySelector<HTMLElement>('.xgplayer-poster');
    if (poster) poster.style.opacity = '1';
  };

  return (
    <>
      <div
        className={`${styles.card} ${selectable ? styles.selectable : ''}`}
        style={previewBackground ? { background: previewBackground } : undefined}
        onClick={handleCardClick}
      >
        {isVideo ? (
          <div
            ref={videoHostRef}
            className={styles.cardVideoBox}
            onMouseEnter={handleVideoMouseEnter}
            onMouseLeave={handleVideoMouseLeave}
          >
            <VideoCom
              volume={0}
              url={displayUrl}
              autoplay={false}
              closeVideoClick
              className={styles.cardVideo}
              poster={posterSrc}
              videoFillMode="cover"
            >
              {({ play, pause, seek }) => {
                videoControlRef.current = { play, pause, seek };
                return <div />;
              }}
            </VideoCom>
            {/* 隐藏的 NextImage 预加载结果封面，沿用原 onResultImageLoad 关闭 loading 遮罩的机制 */}
            {resultUrl ? (
              <NextImage
                src={posterSrc}
                alt=""
                width={1}
                height={1}
                sizes="1px"
                className={styles.posterPreload}
                onLoad={onResultImageLoad}
              />
            ) : null}
          </div>
        ) : (
          <NextImage
            src={posterSrc}
            alt=""
            height="100%"
            width="100%"
            sizes="240px"
            className={styles.cardImage}
            style={imageObjectFit === 'contain' ? { objectFit: 'contain' } : undefined}
            onLoad={resultUrl ? onResultImageLoad : undefined}
          />
        )}

        {suffixLabel && (
          <div className={styles.suffixBadge} aria-hidden>
            {suffixLabel}
          </div>
        )}

        {showLoadingOverlay && (
          <div className={styles.overlay}>
            <div className={styles.overlayContent}>
              <IconFont
                name="loadingCircleBold"
                fontSize={24}
                className={styles.loadingIcon}
              />
              <span className={styles.overlayText}>
                {resultImageLoading ? languageStore?.smartcatTranslationText((v) => v.RoboNeo_Web_loading) : languageStore?.smartcatTranslationText((v) => v.processing)}
              </span>
            </div>
          </div>
        )}

        {status === 'success' && !resultImageLoading && (
          <div className={`${styles.badge} ${styles.badgeSuccess}`}>
            <IconFont
              name="checkV2Bold"
              fontSize={12}
              className={styles.checkMark}
              color="var(--content-primary)"
            />
            <span className={styles.badgeText}>{languageStore?.smartcatTranslationText((v) => v.RoboNeo_web_confirm)}</span>
          </div>
        )}

        {(status === 'failed' || status === 'unrecognized') && (
          <div className={`${styles.badge} ${styles.badgeError}`}>
            <span className={styles.badgeText}>
              {status === 'failed' ? languageStore?.smartcatTranslationText((v) => v.roboneo_web_generation_failed) : errorText}
            </span>
          </div>
        )}

        {!selectable && (
          <div className={styles.deleteBtn} onClick={handleDeleteClick}>
            <IconFont
              color="var(--content-primary)"
              name="crossBlack"
              fontSize={14}
            />
          </div>
        )}

        {selectable && (
          <div
            className={`${styles.checkbox} ${
              selected ? styles.checked : styles.unchecked
            }`}
          >
            {selected && (
              <IconFont name="checkBoxMark" fontSize={10} color="var(--content-primary)" />
            )}
          </div>
        )}
      </div>
      <Modal
        isModalOpen={isModalOpen}
        type="warning"
        close={closeModal}
        cancel={closeModal}
        confirm={() => {
          closeModal();
          onDelete?.();
        }}
      >
        <div className={styles.modalContent}>
          {languageStore?.smartcatTranslationText((v) => v.ai_enhance_confirm_deletion_dialog)}
          ?
        </div>
      </Modal>
    </>
  );
}
